import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import classNames from 'classnames';

import useCustomNotify from 'hooks/useCustomNotify';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';

import { Dialog } from 'components/common';
import UpsAgreements from './UpsAgreements';
import WarehousePresetCreateDialog from 'pages/warehousePresets/components/WarehousePresetCreateDialog';

import { getDomesticCountryCode } from 'services/helpers/countryCode';

import { SUPPORTED_LANGUAGE } from 'services/helpers/constants';

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  languageContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '4px',
  },
  language: {
    color: theme.palette.primary.main,
    padding: '0rem 0',
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  activeLanguage: {
    fontWeight: 900,
  },
  divider: {
    borderRight: `1px solid ${theme.palette.primary.main}`,
  },
}));

interface AgreementTitleProps {
  activeLanguage: string;
  setLanguage: (language: string) => void;
}

const AgreementTitle = ({ activeLanguage, setLanguage }: AgreementTitleProps) => {
  const classes = useStyles();

  const domesticCountryCode = getDomesticCountryCode();

  return (
    <Box className={classes.titleContainer}>
      <DialogTitle>Activate UPS</DialogTitle>
      {domesticCountryCode !== 'US' && (
        <Box className={classes.languageContainer}>
          {SUPPORTED_LANGUAGE.map((language, idx) => (
            <React.Fragment key={language}>
              <Typography
                onClick={() => setLanguage(language)}
                className={classNames(classes.language, {
                  [classes.activeLanguage]: language === activeLanguage,
                })}>
                {language}
              </Typography>
              {idx < SUPPORTED_LANGUAGE.length - 1 && (
                <Divider orientation="vertical" flexItem className={classes.divider} />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

const Note = () => (
  <Typography>
    UPS, the UPS brandmark, UPS Ready<sup>®</sup>, and the color brown are trademarks of United
    Parcel Service of America, Inc. All Rights Reserved.
  </Typography>
);

const UpsActivationFlow = (props) => {
  const { open, onConfirm, onCancel, upsId } = props;

  const dataProvider = useDataProvider();
  const notify = useCustomNotify();

  const [language, setLanguage] = useState('en');
  const [defaultId, setDefaultId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openWarehouseModal, setOpenWarehouseModal] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  useEffect(() => {
    if (upsId) {
      setDefaultId(upsId);
    }
  }, [upsId]);

  const getWarehouse = async () => {
    try {
      const { data: warehouses } = await dataProvider.getList('warehousePresets', {
        pagination: { perPage: 1 },
      });
      return warehouses;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error getting senders address';
      notify(errorMessage, 'error');
    }
  };

  const updateShipmentAccounts = async () => {
    try {
      await dataProvider.getList('shipmentAccounts', { filter: { status: 'ACTIVE,PROMOTIONAL' } });
    } catch (error: any) {
      const errorMessage = error?.message || 'Error updating shipment accounts';
      notify(errorMessage, 'error');
    }
  };

  const onUpsEnablingConfirm = async () => {
    const warehouses = await getWarehouse();
    if (warehouses.length > 0) {
      updateShipmentAccount(defaultId);
    } else {
      setOpenWarehouseModal(true);
    }
  };

  const updateShipmentAccount = (id) => {
    setSubmitting(true);
    return dataProvider
      .create(
        'promoteShipmentAccount',
        {
          id,
        },
        { id: defaultId }
      )
      .then(() => {
        updateShipmentAccounts();
        setOpenConfirmationDialog(true);
      })
      .catch((error) => {
        onCancel();
        notify(typeof error === 'string' ? error : error.message || 'Error', 'warning');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onWarehousesCreated = async () => {
    await updateShipmentAccount(defaultId);
    setOpenWarehouseModal(false);
  };

  const handleConfirm = () => {
    setOpenConfirmationDialog(false);
    onConfirm();
  };
  return (
    <>
      <Dialog
        title="Congratulations!"
        open={!!openConfirmationDialog}
        onCancel={handleConfirm}
        submitting={submitting}
        cancelSize={12}
        cancelText="OK">
        <Typography>
          UPS has been successfully activated. You may now purchase your label
        </Typography>
      </Dialog>
      <WarehousePresetCreateDialog
        {...props}
        onSubmitted={onWarehousesCreated}
        open={openWarehouseModal}
        basePath={props.match.url}
        onClose={() => setOpenWarehouseModal(false)}
      />
      <Dialog
        title={<AgreementTitle activeLanguage={language} setLanguage={setLanguage} />}
        note={<Note />}
        open={!!open}
        confirmText="I Accept"
        onConfirm={onUpsEnablingConfirm}
        onCancel={onCancel}
        submitting={submitting}>
        <UpsAgreements language={language} />
      </Dialog>
    </>
  );
};
export default UpsActivationFlow;
